import { useLocalStorageState } from 'ahooks';
import { Button, Form, Input, message } from 'antd';
import { useState } from 'react';

import InputRemoveIcon from '@/components/icons/InputRemoveIcon';
// import Button from '@/components/common/Button';
import { UseTranslateType } from '@/hooks/useTranslate';
import useLoginStore from '@/store/Login';
import { isAllDigits, isValidEmail, passwordReg, phoneReg } from '@/utils';

import CommonModal from '..';
import CloseIcon from '../icon/close';
import InputError from './components/InputError';
import { SetupPassword } from './components/SetupPassword';
import { VerifyCode } from './components/VerifyCode';
import styles from './style.module.scss';

interface ContentProps {
  t: UseTranslateType;
  onClose: () => void;
}

type FieldType = {
  accountId?: string;
  code?: string;
  password?: string;
};

const Content = ({ t, onClose }: ContentProps) => {
  const [accountId, setAccountId] = useLocalStorageState('accountId', {
    defaultValue: '',
  });
  const {
    sendCodeState,
    countDown,
    loginUIState,
    startCountDown,
    startEmailCountDown,
    loginLoading,
    salt,
    loginWithCode,
    isNeedSetupPassword,
    setUIState,
    savePassword,
  } = useLoginStore((state) => ({
    sendCodeState: state.sendCodeState,
    countDown: state.countDown,
    loginUIState: state.loginUIState,
    startCountDown: state.startCountDown,
    startEmailCountDown: state.startEmailCountDown,
    loginLoading: state.loginLoading,
    salt: state.salt,
    loginWithCode: state.loginWithCode,
    isNeedSetupPassword: state.isNeedSetupPassword,
    setUIState: state.setUIState,
    savePassword: state.savePassword,
  }));
  const [emailOrPhoneInput, setEmailOrPhoneInput] = useState(true);

  const [codeErrorMsg, setCodePasswordErrorMsg] = useState('');
  const [setPasswordErrorMsg, setupPasswordErrorMsg] = useState('');

  const phoneOrEmailInputUI = () => {
    return (
      <div>
        <div>
          <span className="text-[24px] text-app-primary font-sans font-[700] leading-[32px] select-none">
            {t('merror.login.password.forget').replace(/\?|\？/g, '')}
          </span>
        </div>

        <div className="mt-8 py-2">
          <p className="mb-2 body-style">{t('merror.login.phone.input.title')}</p>
          <Form
            name="codeLoginForm"
            initialValues={{ remember: true }}
            onFinish={onFinish}
            autoComplete="off"
            id="phone-login-form"
            className="login-form"
          >
            <Form.Item<FieldType>
              name="accountId"
              validateStatus={emailOrPhoneInput ? '' : 'error'}
            >
              <Input
                placeholder={t('merror.tip.pleaseInput')}
                allowClear={{
                  clearIcon: <InputRemoveIcon></InputRemoveIcon>,
                }}
                style={{
                  fontWeight: 'normal !important',
                }}
                className={styles['input']}
                onChange={(e: any) => setAccountId(e.target.value)}
                onClear={() => {
                  setEmailOrPhoneInput(true);
                }}
              />
            </Form.Item>

            <div className="">
              {emailOrPhoneInput ? (
                <div className="h-8"></div>
              ) : (
                <div className="pt-1 pb-5">
                  <InputError message={t('merror.login.phone.email.error')}></InputError>
                </div>
              )}
            </div>

            <Form.Item>
              <Button
                loading={loginLoading}
                type="primary"
                htmlType="submit"
                className="w-[100%] button-style h-[48px] "
                style={{ height: '48px' }}
              >
                <span className="button-label-style ">{t('merror.login.next')}</span>
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    );
  };

  const emailOrPhoneInputValidator = (accountId: string | undefined) => {
    if (!accountId) {
      return false;
    }
    return isValidEmail(accountId) || phoneReg(accountId);
  };

  const checkLoginAndSendCode = async () => {
    setUIState(3);
    await sendCode();
  };
  const sendCode = async () => {
    if (accountId) {
      // 判断是否全为数字
      if (isAllDigits(accountId)) {
        // 全是数字情况下，当作手机号处理。判断手机号是否正确
        if (!phoneReg(accountId)) {
          // return message.error(t('merror.login.rightPhone'));
          return message.error(t('boom_0.7_notvalid'));
        } else {
          const res = await startCountDown(accountId);
          if (res.code === 10103011) {
            return message.open({
              content: t('merror.login.input.send.code'),
              icon: null,
            });
          }
        }
      } else {
        // 不全是数字时，认为是邮箱
        if (isValidEmail(accountId)) {
          const res = await startEmailCountDown(accountId);
          if (res.code === 10103011) {
            return message.open({
              content: t('merror.login.input.send.code'),
              icon: null,
            });
          }
        } else {
          return message.error(t('boom_0.7_notvalid'));
        }
      }
    } else {
      return message.error(t('boom_0.7_notvalid'));
    }
  };

  const handleVerifySuccess = async (code: string) => {
    const loginRes = await loginWithCode(accountId, code);

    if (loginRes.code !== 200) {
      if (loginRes.code === 10103004) {
        setCodePasswordErrorMsg(t('merror.login.code.error'));
      }
      if (loginRes.code === 12301410) {
        setCodePasswordErrorMsg(t('Errors-exceeds-the-Limit-please-try-again-5-minute'));
      }
      return;
    }

    setCodePasswordErrorMsg('');

    setUIState(5);
    return;
  };

  const onCodeValuesChange = (values: { code: string }) => {
    if (values.code.length >= 0) {
      setCodePasswordErrorMsg('');
    }
  };
  const onSetupPasswordValuesChange = (values: { password: string }) => {
    if (values.password.length >= 0) {
      setupPasswordErrorMsg('');
    }
  };
  const handleSetupPasswordSuccess = async (values: { password: string }) => {
    if (!values.password) {
      setupPasswordErrorMsg(t('merror.login.inputPsd'));
      return;
    }

    if (!passwordReg(values.password)) {
      setupPasswordErrorMsg(t('merror.login.password.desc'));
      return;
    }
    // 密码设置成功后，直接登录
    const res = await savePassword(accountId, values.password, salt);
    if (res.code === 200) {
      // handleLoginSuccess();
      // 退出重新登录。、
      message.success(t('Change-Password-Changed-Successfully'));
      onClose();
      setTimeout(() => {
        useLoginStore.getState().logout();
        window.location.href = window.location.origin + '/home';
      }, 500);
    } else {
      setupPasswordErrorMsg(res.message);
    }
  };

  const onFinish = async (values: any) => {
    if (!values.accountId) return message.error(t('merror.login.phone.email'));

    // 校验账号输入是否合法
    const emailOrPhoneInputValidate = emailOrPhoneInputValidator(values.accountId);

    if (!emailOrPhoneInputValidate)
      return setEmailOrPhoneInput(emailOrPhoneInputValidate);

    checkLoginAndSendCode();
  };

  return (
    <div className=" px-[60px] pt-[48px] pb-[52px] relative forget-pwd">
      <div className="absolute top-[16px] right-[16px] cursor-pointer" onClick={onClose}>
        <CloseIcon />
      </div>
      {loginUIState === 1 ? phoneOrEmailInputUI() : <> </>}

      {(loginUIState === 3 || loginUIState === 6) && (
        <VerifyCode
          t={t}
          accountId={accountId}
          sendCodeState={sendCodeState}
          countDown={countDown}
          isHasPassword={salt}
          loading={loginLoading}
          inputErrorMsg={codeErrorMsg}
          onValuesChange={onCodeValuesChange}
          onSendCode={sendCode}
          onVerifySuccess={handleVerifySuccess}
          onClearVerifyInput={() => {
            setCodePasswordErrorMsg('');
          }}
        />
      )}

      {loginUIState === 5 && (
        <SetupPassword
          t={t}
          loading={loginLoading}
          inputErrorMsg={setPasswordErrorMsg}
          onValuesChange={onSetupPasswordValuesChange}
          onSetupSuccess={handleSetupPasswordSuccess}
        />
      )}
    </div>
  );
};

export function forgetPasswordModalShow() {
  CommonModal.show((t) => ({
    width: 480,
    customPadding: {
      x: 0,
      y: 0,
    },
    footer: false,
    requireLogin: false, // 忘记密码弹窗不需要登录验证
    content: (close) => (
      <Content
        t={t}
        onClose={() => {
          close();
        }}
      />
    ),
  }));
}
